import { ChangeEvent, lazy, Suspense, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { hasSupabaseConfig, supabase } from "./lib/supabase";
import { defaultCampaign, GiveawayCampaign } from "./types/giveaway";
import { Field } from "./components/Field";
import { PageIntro } from "./components/PageIntro";
import { DateTextField } from "./components/DateTextField";
import { compareYMD, formatDisplayDate, normalizeDisplayDate, parseDisplayDate } from "./lib/dates";
// Each module tab loads when first opened, so the giveaway screen and sign-in
// load only what they need.
const StoreLinks = lazy(() => import("./modules/StoreLinks"));
const PageSettings = lazy(() => import("./modules/PageSettings"));
const AppFlags = lazy(() => import("./modules/AppFlags"));
const Content = lazy(() => import("./modules/Content"));
const Skins = lazy(() => import("./modules/Skins"));
const Pages = lazy(() => import("./modules/Pages"));
const Media = lazy(() => import("./modules/Media"));
const Styles = lazy(() => import("./modules/Styles"));
import { type Site } from "./lib/sites";

// One admin, one sign-in, a tab per module. Giveaways is the original screen,
// unchanged. The other tabs edit tables guarded by is_platform_admin() (the
// rule every admin policy in the database uses), which is a separate check
// from giveaway_admins; a tab says so plainly if the account lacks it.
type Tab = "giveaways" | "content" | "pages" | "media" | "styles" | "skins" | "store-links" | "page-settings" | "app-flags";
const TABS: { id: Tab; label: string }[] = [
  { id: "giveaways", label: "Giveaways" },
  { id: "content", label: "Content" },
  { id: "pages", label: "Pages" },
  { id: "media", label: "Media" },
  { id: "styles", label: "Styles" },
  { id: "skins", label: "Skins" },
  { id: "store-links", label: "Store Links" },
  { id: "page-settings", label: "Page Settings" },
  { id: "app-flags", label: "App Flags" },
];

function toInputDate(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toIso(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function campaignStatus(campaign: GiveawayCampaign) {
  const now = Date.now();
  const starts = campaign.starts_at ? new Date(campaign.starts_at).getTime() : null;
  const ends = campaign.ends_at ? new Date(campaign.ends_at).getTime() : null;
  if (!campaign.enabled) return "Hidden";
  if (starts && starts > now) return "Scheduled";
  if (ends && ends <= now) return "Ended";
  return "Visible now";
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [email, setEmail] = useState("");
  const [campaigns, setCampaigns] = useState<GiveawayCampaign[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [draft, setDraft] = useState<GiveawayCampaign>({ ...defaultCampaign });
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("Ready.");
  const [tab, setTab] = useState<Tab>("giveaways");
  // Sites (Phase B): Content and Page Settings edit one site at a time. Empty
  // before the sites migration, and the tabs then work as they always did.
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState<string>(() => { try { return localStorage.getItem("fp-admin-site") ?? ""; } catch { return ""; } });
  const site = sites.find((s) => s.id === siteId) ?? sites.find((s) => s.key === "beta") ?? sites[0] ?? null;
  function chooseSite(id: string) {
    setSiteId(id);
    try { localStorage.setItem("fp-admin-site", id); } catch { /* remembered for this visit only */ }
  }
  const [platformAdmin, setPlatformAdmin] = useState(false);

  const selected = useMemo(
    () => campaigns.find((item) => item.id === selectedId),
    [campaigns, selectedId],
  );

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!supabase || !session) { setLoading(false); return; }
    void loadCampaigns(session);
  }, [session]);

  useEffect(() => {
    if (selected) setDraft({ ...selected });
  }, [selected]);

  // "*": publishing_mode exists only after migration 20260925006.
  async function loadSites() {
    const { data: siteRows } = await supabase!.from("sites").select("*").eq("status", "active").order("name");
    setSites((siteRows ?? []) as Site[]);
  }

  async function loadCampaigns(activeSession: Session) {
    setLoading(true);
    const { data: admin } = await supabase!.from("giveaway_admins").select("user_id").eq("user_id", activeSession.user.id).maybeSingle();
    const authorized = Boolean(admin);
    setIsAdmin(authorized);
    const { data: platform } = await supabase!.rpc("is_platform_admin");
    setPlatformAdmin(platform === true);
    await loadSites();
    if (authorized) {
      const { data, error } = await supabase!.from("giveaway_campaigns").select("*").order("display_order").order("created_at");
      if (error) setMessage(error.message);
      const rows = (data ?? []) as GiveawayCampaign[];
      setCampaigns(rows);
      if (rows.length) setSelectedId((current) => current || rows[0].id!);
    }
    setLoading(false);
  }

  function update<K extends keyof GiveawayCampaign>(key: K, value: GiveawayCampaign[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function requestMagicLink() {
    if (!supabase || !email) return;
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
    setMessage(error ? error.message : "Check your email for the secure sign-in link.");
  }

  function newCampaign() {
    const nextNumber = campaigns.length + 1;
    setSelectedId("");
    setDraft({
      ...defaultCampaign,
      id: undefined,
      slug: `giveaway-${nextNumber}`,
      prize_name: `Giveaway ${nextNumber}`,
      prize_short: `Giveaway ${nextNumber}`,
      enabled: false,
      display_order: (campaigns.at(-1)?.display_order ?? 0) + 10,
    });
    setMessage("New giveaway draft created. It remains hidden until enabled and saved.");
  }

  async function uploadImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !supabase || !isAdmin) return;
    if (!file.type.startsWith("image/") || file.size > 4 * 1024 * 1024) { setMessage("Choose an image no larger than 4 MB."); return; }
    const safeName = file.name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
    const path = `giveaways/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from("giveaway-products").upload(path, file);
    if (error) { setMessage(error.message); return; }
    const { data } = supabase.storage.from("giveaway-products").getPublicUrl(path);
    update("prize_image_url", data.publicUrl);
    setMessage("Image uploaded. Save the giveaway to publish the change.");
  }

  async function save() {
    if (!supabase || !isAdmin || !session) return;
    // The two dates are shown on the public page as text: store them as
    // "December 24, 2026". A hidden draft may leave them empty; a giveaway
    // switched on needs both, and winners can't be named before entries close.
    const close = parseDisplayDate(draft.entries_close);
    const named = parseDisplayDate(draft.winners_named);
    if (draft.entries_close.trim() && !close) { setMessage("Entries close text is not a date. Type e.g. 12/24/2026 or pick one."); return; }
    if (draft.winners_named.trim() && !named) { setMessage("Winners announced text is not a date. Type e.g. 12/26/2026 or pick one."); return; }
    if (draft.enabled && (!close || !named)) { setMessage("A giveaway that is switched on needs both dates."); return; }
    if (close && named && compareYMD(named, close) < 0) { setMessage("Winners can't be announced before entries close."); return; }
    setSaving(true);
    const payload = {
      ...draft,
      entries_close: close ? formatDisplayDate(close) : "",
      winners_named: named ? formatDisplayDate(named) : "",
      starts_at: toIso(draft.starts_at),
      ends_at: toIso(draft.ends_at),
      updated_by: session.user.id,
    };
    delete payload.id;
    const query = draft.id
      ? supabase.from("giveaway_campaigns").update(payload).eq("id", draft.id).select().single()
      : supabase.from("giveaway_campaigns").insert(payload).select().single();
    const { data, error } = await query;
    if (error) setMessage(error.message);
    if (data) {
      const saved = data as GiveawayCampaign;
      setCampaigns((current) => [...current.filter((item) => item.id !== saved.id), saved].sort((a, b) => a.display_order - b.display_order));
      setSelectedId(saved.id!);
      setDraft(saved);
      setMessage("Giveaway saved to Supabase.");
    }
    setSaving(false);
  }

  async function removeCampaign() {
    if (!supabase || !draft.id) return;
    if (!window.confirm(`Delete “${draft.prize_name}”? This cannot be undone.`)) return;
    const { error } = await supabase.from("giveaway_campaigns").delete().eq("id", draft.id);
    if (error) { setMessage(error.message); return; }
    const remaining = campaigns.filter((item) => item.id !== draft.id);
    setCampaigns(remaining);
    setSelectedId(remaining[0]?.id ?? "");
    setDraft(remaining[0] ?? { ...defaultCampaign, id: undefined, enabled: false });
    setMessage("Giveaway deleted.");
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify({ giveaways: campaigns }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "fieldprove-giveaways.json"; a.click();
    URL.revokeObjectURL(url);
  }

  if (!hasSupabaseConfig) return <main className="center-card"><h1>Supabase configuration required</h1><p>Copy <code>.env.example</code> to <code>.env.local</code> and add the project URL and anon key.</p></main>;
  if (loading) return <main className="center-card"><h1>Loading FieldProve Admin…</h1></main>;
  if (!session) return <main className="center-card"><p className="eyebrow">FieldProve administration</p><h1>FieldProve Admin</h1><p>Enter the approved administrator email. Supabase will send a secure sign-in link.</p><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@fieldprove.com"/><button onClick={requestMagicLink}>Send secure sign-in link</button><p className="status">{message}</p></main>;
  if (!isAdmin) return <main className="center-card"><h1>Administrator access required</h1><p>{session.user.email} is signed in but is not listed in <code>giveaway_admins</code>.</p><button onClick={() => supabase!.auth.signOut()}>Sign out</button></main>;

  return <main>
    <header className="topbar"><div className="brand"><b>FP</b><strong>Field<span>Prove</span></strong></div><div>{sites.length > 1 && (tab === "content" || tab === "pages" || tab === "styles" || tab === "page-settings") && <label className="site-picker"><span>Site</span><select value={site?.id ?? ""} onChange={(e) => chooseSite(e.target.value)} aria-label="Site being edited">{sites.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.hosts.find((h) => !h.endsWith(".pages.dev")) ?? s.key})</option>)}</select></label>}<span>{session.user.email}</span><button className="link-button" onClick={() => supabase!.auth.signOut()}>Sign out</button></div></header>
    <nav className="module-tabs wrap" aria-label="Admin modules">
      {TABS.map((t) => <button key={t.id} className={t.id === tab ? "active" : ""} aria-current={t.id === tab ? "page" : undefined} onClick={() => setTab(t.id)}>{t.label}</button>)}
    </nav>
    {tab !== "giveaways" && !platformAdmin && <>
      <PageIntro eyebrow="Access" title={TABS.find((t) => t.id === tab)!.label} text="This tab edits settings for the website and the app." summary="Platform admin role required" message="Ask the database owner to grant the role."/>
      <div className="wrap module-page"><section className="editor"><div className="panel-heading"><div><p className="eyebrow">Access</p><h2>Platform admin role required</h2></div></div><p className="module-intro">{session.user.email} can manage giveaways, but this tab edits tables that only accounts with <code>platform_role</code> admin or owner may change. Set it in Supabase: <code>update public.profiles set platform_role = 'admin' where id = '{session.user.id}';</code></p></section></div>
    </>}
    <Suspense fallback={<section className="intro wrap"><p className="eyebrow">Loading…</p></section>}>
    {tab === "content" && platformAdmin && <Content key={site?.id ?? "all"} session={session} site={site}/>}
    {tab === "pages" && platformAdmin && <Pages key={site?.id ?? "all"} session={session} site={site} onSiteChanged={loadSites}/>}
    {tab === "media" && platformAdmin && <Media session={session}/>}
    {tab === "styles" && platformAdmin && <Styles key={site?.id ?? "all"} session={session} site={site}/>}
    {tab === "skins" && platformAdmin && <Skins session={session}/>}
    {tab === "store-links" && platformAdmin && <StoreLinks session={session}/>}
    {tab === "page-settings" && platformAdmin && <PageSettings key={site?.id ?? "all"} session={session} site={site}/>}
    {tab === "app-flags" && platformAdmin && <AppFlags/>}
    </Suspense>
    {tab === "giveaways" && <>
    <PageIntro eyebrow="Supabase-connected dashboard" title="Control every FieldProve giveaway." text="Each giveaway is one unique product. Add different products, schedule each one, and decide which appear on the landing page." summary={`${campaigns.length} unique product${campaigns.length === 1 ? "" : "s"}`} message={message}/>
    <div className="manager wrap">
      <nav className="campaign-list" aria-label="Giveaways">
        <div className="campaign-list-heading"><div><p className="eyebrow">Giveaway products</p><strong>{campaigns.length} unique</strong></div><button onClick={newCampaign}>+ Add product</button></div>
        {campaigns.map((item) => <button key={item.id} className={`campaign-item ${item.id === selectedId ? "selected" : ""}`} onClick={() => setSelectedId(item.id!)}><span>{item.prize_short}</span><small className={campaignStatus(item).toLowerCase().replaceAll(" ", "-")}>{campaignStatus(item)}</small><i>{item.winner_count} prize{item.winner_count === 1 ? "" : "s"}</i></button>)}
        {!campaigns.length && <p className="empty-list">No giveaways yet. Create the first one.</p>}
      </nav>
      <div className="workspace">
        <section className="editor">
          <div className="panel-heading"><div><p className="eyebrow">Selected giveaway</p><h2>{draft.id ? draft.prize_short : "New giveaway"}</h2></div><label className="toggle"><input type="checkbox" checked={draft.enabled} onChange={(e) => update("enabled", e.target.checked)}/><span>{draft.enabled ? "Enabled" : "Hidden"}</span></label></div>
          <div className="grid-2"><Field label="Internal slug"><input value={draft.slug} onChange={(e) => update("slug", e.target.value)}/></Field><Field label="Display order"><input type="number" value={draft.display_order} onChange={(e) => update("display_order", Number(e.target.value))}/></Field></div>
          <div className="grid-2"><Field label="Visible from" hint="Leave blank to start immediately."><input type="datetime-local" value={toInputDate(draft.starts_at)} onChange={(e) => update("starts_at", e.target.value || null)}/></Field><Field label="Visible until" hint="Leave blank for no automatic end."><input type="datetime-local" value={toInputDate(draft.ends_at)} onChange={(e) => update("ends_at", e.target.value || null)}/></Field></div>
          <div className="schedule-summary"><strong>Landing-page status: {campaignStatus(draft)}</strong><span>Enabled + within its scheduled window = visible.</span></div>
          <div className="grid-2"><Field label="Number of winners for this product"><input type="number" min="1" max="99" value={draft.winner_count} onChange={(e) => update("winner_count", Math.max(1, Number(e.target.value)))}/></Field><Field label="Retail value"><input value={draft.prize_value} onChange={(e) => update("prize_value", e.target.value)}/></Field></div>
          <Field label="Full product name"><input value={draft.prize_name} onChange={(e) => update("prize_name", e.target.value)}/></Field>
          <Field label="Short product name" hint="Used in the headline."><input value={draft.prize_short} onChange={(e) => update("prize_short", e.target.value)}/></Field>
          <Field label="Product specifications"><textarea rows={3} value={draft.prize_specs} onChange={(e) => update("prize_specs", e.target.value)}/></Field>
          <Field label="Prize hook"><textarea rows={2} value={draft.prize_hook} onChange={(e) => update("prize_hook", e.target.value)}/></Field>
          <div className="image-control"><img src={draft.prize_image_url} alt="Current giveaway product"/><div><label className="upload">Choose image<input type="file" accept="image/*" onChange={uploadImage}/></label><button className="link-button" onClick={() => update("prize_image_url", "/default-backpack.webp")}>Restore Klein image</button><p>Transparent PNG or WEBP, maximum 4 MB.</p></div></div>
          <Field label="How to qualify"><textarea rows={3} value={draft.entry_rule} onChange={(e) => update("entry_rule", e.target.value)}/></Field>
          <Field label="How winners are chosen"><textarea rows={3} value={draft.judging_rule} onChange={(e) => update("judging_rule", e.target.value)}/></Field>
          <div className="grid-2"><DateTextField label="Entries close" value={draft.entries_close} onChange={(v) => update("entries_close", v)}/><DateTextField label="Winners announced" value={draft.winners_named} onChange={(v) => update("winners_named", v)}/></div>
          <Field label="Rules URL"><input type="url" value={draft.rules_url} onChange={(e) => update("rules_url", e.target.value)}/></Field>
          {message !== "Ready." && <p className="status">{message}</p>}
          <div className="actions"><button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save giveaway"}</button><button className="secondary" onClick={exportJson}>Export all</button>{draft.id && <button className="danger" onClick={removeCampaign}>Delete</button>}</div>
        </section>
        <aside className="preview"><p className="eyebrow">Selected giveaway preview</p>{campaignStatus(draft) === "Visible now" ? <div className="giveaway"><div className="prize"><i>{draft.winner_count}</i><img src={draft.prize_image_url} alt={draft.prize_name}/></div><div><h2>{draft.winner_count} testers get a {draft.prize_short}</h2><p>{draft.prize_specs}. {draft.prize_value}.</p><h3>{draft.prize_hook}</h3><h4>How to qualify</h4><p>{draft.entry_rule}</p><h4>How we pick</h4><p>{draft.judging_rule} Write like it matters, because it does.</p><p>Entries close {normalizeDisplayDate(draft.entries_close) ?? draft.entries_close}. Winners announced {normalizeDisplayDate(draft.winners_named) ?? draft.winners_named}.</p><a href={draft.rules_url}>Full rules →</a></div></div> : <div className="hidden-state"><h2>{campaignStatus(draft)}</h2><p>This giveaway is not currently shown on the public landing page.</p></div>}</aside>
      </div>
    </div>
    </>}
  </main>;
}
